import { RefObject, useEffect, useRef } from "react";
import { useAuth } from "../../firebase/AuthProvider";
import { useChatConnection } from "../../contexts/ChatConnectionContext";
import { ChatActivity, markChatActivityRead } from "../../services/chatService/ChatService";

// ReceiveReaction precedes activity persistence. Wait for ReceiveChatActivity,
// then acknowledge only the specific reaction whose message is actually read.
export function useVisibleReactionRead(
    containerRef: RefObject<HTMLDivElement>,
    conversationId: number,
    messageAttribute: "data-chat-message-id" | "data-thread-message-id",
    content: unknown,
    enabled: boolean,
) {
    const { currentUser } = useAuth();
    const { connection } = useChatConnection();
    const pending = useRef(new Map<number, ChatActivity>());
    const inFlight = useRef(new Set<number>());
    const attempt = useRef<() => void>(() => undefined);

    useEffect(() => {
        // A new map identifies this account/conversation/connection scope, even
        // if an already-sent acknowledgement finishes after the scope changes.
        const scope = new Map<number, ChatActivity>();
        pending.current = scope;
        if (!connection || !currentUser) return;
        const receive = (activity: ChatActivity) => {
            if (activity.conversationId !== conversationId || activity.read || activity.activityType !== "chat.reaction") return;
            const previous = scope.get(activity.id);
            if (previous && previous.createdAt === activity.createdAt && previous.reactionType === activity.reactionType
                && previous.messageId === activity.messageId && previous.actorUserId === activity.actorUserId) return;
            scope.set(activity.id, activity);
            attempt.current();
        };
        connection.on("ReceiveChatActivity", receive);
        return () => {
            connection.off("ReceiveChatActivity", receive);
            if (pending.current === scope) pending.current = new Map();
        };
    }, [connection, conversationId, currentUser]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || !enabled || !currentUser) return;
        let disposed = false;
        let timer = 0;
        const visible = new Set<number>();
        const pendingActivities = pending.current;
        const canRead = (activity: ChatActivity) => !disposed && pending.current === pendingActivities
            && pendingActivities.get(activity.id) === activity && document.visibilityState === "visible"
            && visible.has(activity.messageId);
        const acknowledge = async (activity: ChatActivity) => {
            try {
                const token = await currentUser.getIdToken();
                if (!canRead(activity)) return;
                await markChatActivityRead(token, activity.id);
                // Reaction updates reuse an activity ID. An older response
                // must never consume or hide a newer one.
                if (pending.current !== pendingActivities || pendingActivities.get(activity.id) !== activity) return;
                pendingActivities.delete(activity.id);
                window.dispatchEvent(new CustomEvent("chat-activity-read", { detail: { activityId: activity.id } }));
            } catch (error) {
                console.error("Unable to mark visible reaction as read", error);
            } finally {
                inFlight.current.delete(activity.id);
                if (disposed || (pendingActivities.has(activity.id) && pendingActivities.get(activity.id) !== activity)) attempt.current();
            }
        };
        const schedule = () => {
            window.clearTimeout(timer);
            if (document.visibilityState !== "visible") return;
            timer = window.setTimeout(() => {
                for (const activity of Array.from(pendingActivities.values())) {
                    if (!canRead(activity) || inFlight.current.has(activity.id)) continue;
                    inFlight.current.add(activity.id);
                    void acknowledge(activity);
                }
            }, 400);
        };
        attempt.current = schedule;
        const observer = new IntersectionObserver(entries => {
            for (const entry of entries) {
                const id = Number(entry.target.getAttribute(messageAttribute));
                if (id <= 0) continue;
                if (entry.isIntersecting && entry.intersectionRatio >= 0.55) visible.add(id);
                else visible.delete(id);
            }
            schedule();
        }, { root: container, threshold: [0.55, 0.8, 1] });
        container.querySelectorAll(`[${messageAttribute}]`).forEach(element => observer.observe(element));
        document.addEventListener("visibilitychange", schedule);
        return () => {
            disposed = true;
            attempt.current = () => undefined;
            window.clearTimeout(timer);
            document.removeEventListener("visibilitychange", schedule);
            observer.disconnect();
        };
    }, [connection, containerRef, conversationId, currentUser, messageAttribute, content, enabled]);
}
